<template>
  <div
    v-if="!isAdmin"
    class="flex h-full flex-col items-center justify-center gap-3 py-20 text-center"
  >
    <div
      class="flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500"
    >
      <svg-icon name="huge-unlock" class="h-6 w-6" />
    </div>
    <div class="text-lg font-semibold text-foreground">
      {{ $t("Нет доступа") }}
    </div>
    <div class="max-w-sm text-sm text-muted-foreground">
      {{
        $t(
          "Раздел Face ID доступен только администраторам. Обратитесь к суперадмину для получения доступа."
        )
      }}
    </div>
  </div>
  <div v-else class="flex h-full flex-col gap-6">
    <!-- Stats -->
    <!--    <FaceIdStats :loading="loading" :stats="stats" class="mb-4" />-->

    <!-- Active table -->
    <FaceIdUsersTable
      v-if="activeView === 'users'"
      :users="enrichedUsers"
      :total="enrolledTotal"
      :loading="loading"
      :view-tabs="viewTabs"
      :active-view="activeView"
      v-model:query="userQuery"
      v-model:page="page"
      v-model:page-size="pageSize"
      @open="openFaceId"
      @create="openCreate"
      @update:active-view="activeView = $event"
    />

    <FaceIdAttemptsTable
      v-else-if="activeView === 'attempts'"
      :entries="attempts"
      :total="attemptsTotal"
      :counts="attemptsCounts"
      :user-by-id-map="userByIdMap"
      :loading="attemptsLoading"
      :view-tabs="viewTabs"
      :active-view="activeView"
      v-model:page="attemptsPage"
      v-model:page-size="attemptsPageSize"
      v-model:status-filter="attemptsStatus"
      v-model:query="attemptsQuery"
      :start-date="attemptsStartDate"
      :end-date="attemptsEndDate"
      @update:start-date="attemptsStartDate = $event"
      @update:end-date="attemptsEndDate = $event"
      @open-user="openFaceId"
      @update:active-view="activeView = $event"
    />

    <FormSheet
      v-model:open="drawerOpen"
      :title="drawerTitle"
      :width="560"
      @close="onDrawerClose"
    >
      <UserFaceRecognition
        v-if="selectedUser"
        ref="faceRec"
        :selected-user="selectedUser"
        @update:has-faces="selectedHasFaces = $event"
        @update:uploading="selectedUploading = $event"
      />
      <template #footer>
        <div class="ml-auto flex gap-2">
          <Button variant="outline" @click="drawerOpen = false">
            {{ $t("Закрыть") }}
          </Button>
          <Button
            v-if="selectedHasFaces"
            variant="destructive"
            :disabled="selectedUploading"
            @click="triggerRemoveAll"
          >
            <svg-icon name="huge-danger" class="h-4 w-4" />
            {{ $t("Удалить Face ID") }}
          </Button>
        </div>
      </template>
    </FormSheet>

    <FaceIdCreateDrawer
      v-model:open="createOpen"
      :enrolled-ids="enrolledIds"
      @saved="onCreated"
    />
  </div>
</template>

<script>
import Button from "@/shared/ui/button/Button.vue";
import { FormSheet } from "@/shared/ui/form-sheet";
import UserFaceRecognition from "@/components/face_recognition/UserFaceRecognition.vue";
import { getUserList } from "@/entities/user";
import { listFaces, listAuditHistory } from "@/entities/face-recognition";
import FaceIdUsersTable from "./face-id/FaceIdUsersTable.vue";
import FaceIdAttemptsTable from "./face-id/FaceIdAttemptsTable.vue";
import FaceIdCreateDrawer from "./face-id/FaceIdCreateDrawer.vue";
// import FaceIdStats from "./face-id/FaceIdStats.vue";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";
import { fullName, detectGenderFromIin } from "./face-id/utils";

export default {
  name: "FaceIdTab",
  components: {
    Button,
    FormSheet,
    UserFaceRecognition,
    FaceIdUsersTable,
    FaceIdAttemptsTable,
    FaceIdCreateDrawer,
    BaseTabs,
  },
  props: {
    view: {
      type: String,
      default: "users",
      validator: (v) => ["users", "attempts"].includes(v),
    },
  },
  data() {
    return {
      loading: false,
      attemptsLoading: false,
      enrolledPersons: [],
      enrolledTotal: 0,
      enrolledIds: new Set(),
      allUsers: [],
      userQuery: "",
      page: 1,
      pageSize: 10,
      attempts: [],
      attemptsTotal: 0,
      attemptsCounts: { all: 0, success: 0, failure: 0 },
      attemptsPage: 1,
      attemptsPageSize: 10,
      attemptsStatus: "all",
      attemptsQuery: "",
      attemptsStartDate: "",
      attemptsEndDate: "",
      activeView: this.view,
      drawerOpen: false,
      selectedUser: null,
      selectedHasFaces: false,
      selectedUploading: false,
      createOpen: false,
    };
  },
  computed: {
    userByIdMap() {
      const map = {};
      this.allUsers.forEach((u) => {
        const key = String(u.uuid || u.id || "");
        if (key) map[key] = u;
      });
      return map;
    },
    enrichedUsers() {
      return this.enrolledPersons.map((p) => {
        const pid = String(p.person_id || "");
        const user = this.userByIdMap[pid] || {};
        const attemptsForUser = this.attempts.filter(
          (a) => String(a.person_id || "") === pid
        );
        const total = attemptsForUser.length;
        const success = attemptsForUser.filter(
          (a) => a.status === "success"
        ).length;
        const lastSeen = attemptsForUser[0]?.timestamp || null;
        const successRate = total > 0 ? (success / total) * 100 : null;

        return {
          id: pid,
          person_id: pid,
          identifier: user.identifier || null,
          email: user.email || null,
          role: user.role || null,
          dept: user.dept || user.department || null,
          branch: user.branch || user.organization || null,
          devices: user.devices || [],
          gender:
            user.gender ||
            detectGenderFromIin(user.identifier || user.iin || ""),
          name: fullName(user) || p.person_name || "—",
          faces_count: p.faces_count || 0,
          enrollment_date: p.enrollment_date || null,
          last_seen: lastSeen,
          attempts: total,
          success,
          successRate,
          _raw: { ...user, ...p },
        };
      });
    },
    drawerTitle() {
      if (!this.selectedUser) return "Face ID";
      // const name = this.selectedUser.name || fullName(this.selectedUser);
      return "Face ID";
    },
    viewTabs() {
      // null — чтобы FaceIdUsersTable/FaceIdAttemptsTable полностью скрыли
      // свой внутренний BaseTabs (`v-if="viewTabs"`). Пустой объект `{}` —
      // truthy и вызывает рекурсию reka-ui Tabs. Переключение users/attempts/
      // devices теперь живёт на уровне SettingsIndex через router.
      return null;
    },
    isAdmin() {
      const role = this.$store.state.self_user?.role;
      return role === "ADMIN";
    },
    stats() {
      const today = new Date().toISOString().slice(0, 10);
      const todayAttempts = this.attempts.filter((a) =>
        String(a.timestamp || "").startsWith(today)
      );
      const todaySuccess = todayAttempts.filter(
        (a) => a.status === "success"
      ).length;
      const todayFail = todayAttempts.length - todaySuccess;
      const total = this.attempts.length;
      const successTotal = this.attempts.filter(
        (a) => a.status === "success"
      ).length;
      const successRate = total > 0 ? (successTotal / total) * 100 : null;
      const suspicious = this.attempts.filter((a) => {
        const score =
          typeof a.similarity_score === "number" ? a.similarity_score : 1;
        const s = score > 1 ? score / 100 : score;
        return a.status !== "success" && s < 0.4;
      }).length;
      return {
        enrolledCount: this.enrolledTotal,
        todaySuccess,
        todayFail,
        successRate,
        suspicious,
      };
    },
  },
  mounted() {
    if (!this.isAdmin) return;
    this.refreshAll();
  },
  watch: {
    view(value) {
      // SettingsIndex меняет route → меняем activeView чтобы синхронизировать.
      if (value && value !== this.activeView) {
        this.activeView = value;
      }
    },
    page() {
      this.loadEnrolled();
    },
    pageSize() {
      this.page = 1;
      this.loadEnrolled();
    },
    userQuery() {
      this.page = 1;
      this.loadEnrolled();
    },
    attemptsPage() {
      this.loadAttempts();
    },
    attemptsPageSize() {
      this.attemptsPage = 1;
      this.loadAttempts();
    },
    attemptsStatus() {
      this.attemptsPage = 1;
      this.loadAttempts();
    },
    attemptsStartDate() {
      this.attemptsPage = 1;
      this.loadAttempts();
    },
    attemptsEndDate() {
      this.attemptsPage = 1;
      this.loadAttempts();
    },
    activeView(value) {
      if (value === "users") this.loadEnrolled();
      else if (value === "attempts") this.loadAttempts();
    },
  },
  methods: {
    async refreshAll() {
      this.loading = true;
      this.attemptsLoading = true;
      try {
        await Promise.all([this.loadEnrolled(), this.loadUsers()]);
      } finally {
        this.loading = false;
      }
      try {
        await this.loadAttempts();
      } finally {
        this.attemptsLoading = false;
      }
    },
    async loadEnrolled() {
      this.loading = true;
      try {
        const offset = (this.page - 1) * this.pageSize;
        const params = { limit: this.pageSize, offset };
        const q = (this.userQuery || "").trim();
        if (q) params.personName = q;
        const res = await listFaces(params);
        const persons =
          res?.persons || res?.items || res?.entries || res?.data || [];
        this.enrolledPersons = persons;
        const rawTotal = Number(res?.total);
        this.enrolledTotal =
          Number.isFinite(rawTotal) && rawTotal >= 0
            ? rawTotal
            : persons.length;
        const ids = new Set(this.enrolledIds);
        persons.forEach((p) => {
          if (p.person_id) ids.add(String(p.person_id));
        });
        this.enrolledIds = ids;
      } catch {
        this.enrolledPersons = [];
        this.enrolledTotal = 0;
      } finally {
        this.loading = false;
      }
    },
    async loadUsers() {
      try {
        const data = await getUserList({ page: 1, page_size: 100 });
        if (data?.success && data?.payload) {
          this.allUsers = data.payload.items || [];
        }
      } catch {
        this.allUsers = [];
      }
    },
    async loadAttempts() {
      this.attemptsLoading = true;
      try {
        const offset = (this.attemptsPage - 1) * this.attemptsPageSize;
        const startTime = this.attemptsStartDate
          ? `${this.attemptsStartDate}T00:00:00`
          : undefined;
        const endTime = this.attemptsEndDate
          ? `${this.attemptsEndDate}T23:59:59`
          : undefined;
        const params = { limit: this.attemptsPageSize, offset };
        if (this.attemptsStatus === "success") params.status = "success";
        else if (this.attemptsStatus === "failure") params.status = "failure";
        if (startTime) params.startTime = startTime;
        if (endTime) params.endTime = endTime;

        const countsBase = { startTime, endTime };

        const [pageRes, successRes, failureRes] = await Promise.all([
          listAuditHistory(params),
          this.attemptsStatus === "all"
            ? Promise.resolve(null)
            : listAuditHistory({
                limit: 1,
                offset: 0,
                status: "success",
                ...countsBase,
              }),
          this.attemptsStatus === "all"
            ? Promise.resolve(null)
            : listAuditHistory({
                limit: 1,
                offset: 0,
                status: "failure",
                ...countsBase,
              }),
        ]);

        this.attempts =
          pageRes?.entries ||
          pageRes?.items ||
          pageRes?.history ||
          pageRes?.data ||
          [];
        const rawTotal = Number(pageRes?.total);
        const pageTotal =
          Number.isFinite(rawTotal) && rawTotal >= 0
            ? rawTotal
            : this.attempts.length;
        this.attemptsTotal = pageTotal;

        const toCount = (v) => {
          const n = Number(v);
          return Number.isFinite(n) && n >= 0 ? n : 0;
        };

        if (this.attemptsStatus === "all") {
          const success = this.attempts.filter(
            (a) => a.status === "success"
          ).length;
          this.attemptsCounts = {
            all: pageTotal,
            success,
            failure: pageTotal - success,
          };
        } else {
          const successCount = toCount(successRes?.total);
          const failureCount = toCount(failureRes?.total);
          this.attemptsCounts = {
            all: successCount + failureCount,
            success: successCount,
            failure: failureCount,
          };
        }
      } catch {
        this.attempts = [];
        this.attemptsTotal = 0;
        this.attemptsCounts = { all: 0, success: 0, failure: 0 };
      } finally {
        this.attemptsLoading = false;
      }
    },
    openCreate() {
      if (!this.isAdmin) return;
      this.createOpen = true;
    },
    openFaceId(row) {
      if (!this.isAdmin) return;
      const raw = row._raw || row;
      const rawId = row.person_id || row.uuid || row.id;
      this.selectedUser = {
        ...raw,
        uuid: rawId,
        id: rawId,
        name: row.name || fullName(raw),
      };
      this.selectedHasFaces = false;
      this.selectedUploading = false;
      this.drawerOpen = true;
    },
    async onDrawerClose() {
      this.selectedUser = null;
      this.selectedHasFaces = false;
      this.selectedUploading = false;
      // Перезагружаем актуальный вид после возможных изменений в drawer
      // (добавление/удаление фото, enroll, delete-all).
      if (this.activeView === "users") {
        await this.loadEnrolled();
      } else {
        await this.loadAttempts();
      }
    },
    triggerRemoveAll() {
      this.$refs.faceRec?.removeAll();
    },
    onCreated() {
      this.refreshAll();
    },
  },
};
</script>
